import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

import StatusIndicator from '../../../components/ui/StatusIndicator';

const DuplicateResolutionModal = ({ 
  isOpen, 
  onClose, 
  duplicates = [], 
  onResolve 
}) => {
  const [selectedActions, setSelectedActions] = useState({});
  const [isProcessing, setIsProcessing] = useState(false);

  const actionOptions = [
    { value: 'keep_existing', label: 'Keep existing record', icon: 'Shield', color: 'text-success' },
    { value: 'replace', label: 'Replace with new data', icon: 'RefreshCw', color: 'text-warning' },
    { value: 'merge', label: 'Merge records', icon: 'GitMerge', color: 'text-primary' },
    { value: 'create_new', label: 'Create as new record', icon: 'Plus', color: 'text-accent' }
  ];

  const handleActionChange = (duplicateId, action) => {
    setSelectedActions(prev => ({
      ...prev,
      [duplicateId]: action
    }));
  };

  const handleResolveAll = async () => {
    setIsProcessing(true);
    try {
      await onResolve(selectedActions);
      onClose();
    } catch (error) {
      console.error('Error resolving duplicates:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const getConfidenceColor = (confidence) => {
    if (confidence >= 0.9) return 'text-error';
    if (confidence >= 0.7) return 'text-warning';
    return 'text-success';
  };

  const getConfidenceLabel = (confidence) => {
    if (confidence >= 0.9) return 'Very likely duplicate';
    if (confidence >= 0.7) return 'Possible duplicate';
    return 'Low match confidence';
  };

  if (!isOpen || duplicates?.length === 0) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-background/80 backdrop-blur-sm" onClick={onClose} />
      {/* Modal */}
      <div className="relative bg-card border border-border rounded-lg shadow-moderate w-full max-w-6xl mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-warning/10 rounded-lg flex items-center justify-center">
              <Icon name="AlertTriangle" size={20} className="text-warning" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-foreground">Resolve Duplicate Vendors</h2>
              <p className="text-sm text-muted-foreground">
                {duplicates?.length} potential duplicate{duplicates?.length !== 1 ? 's' : ''} found during import
              </p>
            </div>
          </div>
          
          <Button variant="ghost" size="icon" onClick={onClose}>
            <Icon name="X" size={20} />
          </Button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[70vh]">
          <div className="p-6 space-y-6">
            {duplicates?.map((duplicate, index) => (
              <div key={duplicate?.id} className="border border-border rounded-lg overflow-hidden">
                {/* Duplicate Header */}
                <div className="bg-muted/30 px-6 py-4 border-b border-border">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <span className="text-sm font-medium text-foreground">
                        Duplicate #{index + 1}
                      </span>
                      <StatusIndicator
                        status="needs-review"
                        variant="confidence"
                        confidence={duplicate?.confidence}
                        size="sm"
                      />
                      <span className={`text-xs font-medium ${getConfidenceColor(duplicate?.confidence)}`}>
                        {getConfidenceLabel(duplicate?.confidence)}
                      </span>
                    </div>
                    
                    <div className="text-xs text-muted-foreground">
                      Matched on: {duplicate?.matchedFields?.join(', ')}
                    </div>
                  </div>
                </div>

                {/* Comparison */}
                <div className="p-6">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Existing Record */}
                    <div className="space-y-4">
                      <div className="flex items-center space-x-2">
                        <Icon name="Database" size={16} className="text-success" />
                        <h4 className="font-medium text-foreground">Existing Record</h4>
                        <span className="text-xs text-muted-foreground">
                          Created {new Date(duplicate.existing.createdAt)?.toLocaleDateString('en-GB')}
                        </span>
                      </div>
                      
                      <div className="bg-success/5 border border-success/20 rounded-lg p-4 space-y-3">
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div>
                            <span className="text-muted-foreground">Company:</span>
                            <div className="font-medium text-foreground">{duplicate?.existing?.companyName}</div>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Account Ref:</span>
                            <div className="font-mono text-foreground">{duplicate?.existing?.accountReference}</div>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Contact:</span>
                            <div className="text-foreground">{duplicate?.existing?.contactName || 'Not provided'}</div>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Email:</span>
                            <div className="text-foreground">{duplicate?.existing?.email || 'Not provided'}</div>
                          </div>
                          <div>
                            <span className="text-muted-foreground">VAT Number:</span>
                            <div className="font-mono text-foreground">{duplicate?.existing?.vatNumber || 'Not provided'}</div>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Phone:</span>
                            <div className="text-foreground">{duplicate?.existing?.phone || 'Not provided'}</div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* New Record */}
                    <div className="space-y-4">
                      <div className="flex items-center space-x-2">
                        <Icon name="Upload" size={16} className="text-primary" />
                        <h4 className="font-medium text-foreground">New Import Data</h4>
                        <span className="text-xs text-muted-foreground">From CSV import</span>
                      </div>
                      
                      <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 space-y-3">
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div>
                            <span className="text-muted-foreground">Company:</span>
                            <div className="font-medium text-foreground">{duplicate?.new?.companyName}</div>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Account Ref:</span>
                            <div className="font-mono text-foreground">{duplicate?.new?.accountReference}</div>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Contact:</span>
                            <div className="text-foreground">{duplicate?.new?.contactName || 'Not provided'}</div>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Email:</span>
                            <div className="text-foreground">{duplicate?.new?.email || 'Not provided'}</div>
                          </div>
                          <div>
                            <span className="text-muted-foreground">VAT Number:</span>
                            <div className="font-mono text-foreground">{duplicate?.new?.vatNumber || 'Not provided'}</div>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Phone:</span>
                            <div className="text-foreground">{duplicate?.new?.phone || 'Not provided'}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Action Selection */}
                  <div className="mt-6 pt-6 border-t border-border">
                    <h5 className="text-sm font-medium text-foreground mb-4">Choose resolution action:</h5>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                      {actionOptions?.map((option) => (
                        <label
                          key={option?.value}
                          className={`
                            flex items-center space-x-3 p-3 border rounded-lg cursor-pointer transition-smooth
                            ${selectedActions?.[duplicate?.id] === option?.value
                              ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50 hover:bg-muted/30'
                            }
                          `}
                        >
                          <input
                            type="radio"
                            name={`action-${duplicate?.id}`}
                            value={option?.value}
                            checked={selectedActions?.[duplicate?.id] === option?.value}
                            onChange={(e) => handleActionChange(duplicate?.id, e?.target?.value)}
                            className="sr-only"
                          />
                          <Icon 
                            name={option?.icon} 
                            size={16} 
                            className={selectedActions?.[duplicate?.id] === option?.value ? 'text-primary' : option?.color}
                          />
                          <span className={`text-sm ${selectedActions?.[duplicate?.id] === option?.value ? 'text-primary font-medium' : 'text-foreground'}`}>
                            {option?.label}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-border bg-muted/20">
          <div className="text-sm text-muted-foreground">
            {Object.keys(selectedActions)?.length} of {duplicates?.length} duplicates resolved
          </div>
          
          <div className="flex items-center space-x-3">
            <Button variant="outline" onClick={onClose} disabled={isProcessing}>
              Cancel Import
            </Button>
            
            <Button
              onClick={handleResolveAll}
              loading={isProcessing}
              disabled={Object.keys(selectedActions)?.length !== duplicates?.length}
              iconName="CheckCircle"
              iconPosition="left"
            >
              Resolve All Duplicates
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DuplicateResolutionModal;